import type { CSSProperties, ReactNode, HTMLAttributes } from 'react';

/**
 * Surface — the canonical glass card primitive.
 *
 * Three tones, mapping to the DESIGN.md surface hierarchy:
 *
 *   default — base glass: `surface-glass` (rgba white 6%) + thin border.
 *             Used for most cards — stat tiles, data rows, modal panels.
 *
 *   raised  — `surface-glass-strong` (rgba white 10%) + thin border +
 *             subtle primary-glow shadow. The "level 2" treatment from
 *             DESIGN.md — picked surfaces, hovered states, hero CTAs.
 *
 *   deep    — solid `--surface-deep` (#12121a) — inset feel for inputs
 *             and inset panels inside an already-glassy card.
 *
 * All three are pill-radius-friendly via the `as` prop (button/li/etc.)
 * — Surface doesn't lock you to a div.
 */
type SurfaceTone = 'default' | 'raised' | 'deep';

type SurfaceProps = {
  tone?: SurfaceTone;
  /** When set, the surface gets a primary-tinted ring + glow on hover. */
  interactive?: boolean;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
} & Omit<HTMLAttributes<HTMLDivElement>, 'className' | 'style' | 'children'>;

const TONE_CLASS: Record<SurfaceTone, string> = {
  default: 'glass',
  raised: 'glass-strong',
  // `deep` is a solid surface; no glass utility — set bg via style below.
  deep: 'border border-[var(--color-border)]',
};

export function Surface({
  tone = 'default',
  interactive = false,
  className = '',
  style,
  children,
  ...rest
}: SurfaceProps) {
  const base = TONE_CLASS[tone];
  const radius = 'rounded-2xl';
  const interact = interactive
    ? 'transition hover:border-[var(--color-primary)]/40 hover:shadow-[var(--glow-primary)]'
    : '';
  return (
    <div
      className={`${base} ${radius} ${interact} ${className}`}
      style={{
        ...(tone === 'deep' ? { background: 'var(--surface-deep)' } : null),
        ...(tone === 'raised'
          ? { boxShadow: 'var(--shadow-card)' }
          : null),
        ...style,
      }}
      {...rest}
    >
      {children}
    </div>
  );
}
